import { useMemo } from 'react';
import { DemeterDataSource, DemeterPageElementModel, DemeterPageType, ExchangeType } from '../../../../Generated/Raven-Demeter';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import { ITreeNode } from '../../TreeView/TreeItem';
import TreeView from '../../TreeView/TreeView';
import { IPageElementModalSelection } from './PageElementModalDefinitions';

interface IPageElementExchangeAndCommodityProps {
    selection: IPageElementModalSelection;
    pageElements: DemeterPageElementModel[];
    handleDataSourceSelect: (selected: DemeterDataSource | null | undefined) => void;
    handleExchangeSelect: (selected: ExchangeType | null | undefined) => void;
    handleCommoditySelect: (selectedName: string | null | undefined) => void;
    handleSubCommoditySelect: (selectedName: string | null | undefined) => void;
    track: TrackingEvent;
}

const PageElementExchangeAndCommodity: React.FC<IPageElementExchangeAndCommodityProps> = (props) => {
    const [translations, translate] = useLanguage();

    const equals = (a: any, b: any) => {
        if (a === b) {
            return true;
        }
        if ((a === null || a === undefined) && (b === null || b === undefined)) {
            return true;
        }
        return String(a) === String(b);
    };

    const isMarketIndicators = props.selection.pageType === DemeterPageType.CommodityIndicators || props.selection.pageType === DemeterPageType.HedgeMonitorPro;

    const isFutures = props.selection.pageType === DemeterPageType.Futures;

    const marketIndicatorsTreeNodes: ITreeNode[] = useMemo(() => {
        const map = new Map<string, ITreeNode>();

        props.pageElements.forEach((pageElement) => {
            const { dataSource, exchange, commodity, commodityDisplayName } = pageElement;

            const commodityName = commodityDisplayName ? translate(commodityDisplayName) ?? commodityDisplayName : commodity ?? '';
            const commodityId = `${exchange}|${dataSource}|${commodityName}`;
            const commodityValue = `${commodityId}`;

            if (commodityId && !map.has(commodityId)) {
                map.set(commodityId, {
                    id: commodityId,
                    name: commodityName,
                    value: commodityValue,
                    parentId: '',
                    parentValue: '',
                    column: 'left',
                    selectedProduct:
                        equals(commodityName, props.selection.commodityName) &&
                        equals(exchange, props.selection.exchange) &&
                        equals(dataSource, props.selection.dataSource),
                    expanded: false,
                    order: 0,
                    displayOrder: 0,
                    children: [],
                });
            }
        });

        return Array.from(map.values());
    }, [props.pageElements, props.selection.commodityName, props.selection.dataSource, props.selection.exchange]);

    const futuresTreeNodes: ITreeNode[] = useMemo(() => {
        const map = new Map<string, ITreeNode>();

        props.pageElements.forEach((pageElement) => {
            const { exchange, commodity, commodityDisplayName } = pageElement;

            const exchangeId = `${exchange}`;
            const exchangeName = translations.exchange[exchange!] ? translations.exchange[exchange!] : exchange ?? '';
            const exchangeValue = `${exchange}`;

            const commodityName = commodityDisplayName ? translate(commodityDisplayName) ?? commodityDisplayName : commodity ?? '';
            const commodityId = `${exchange}|${commodityName}`;
            const commodityValue = `${commodityId}`;

            // Exchange node
            if (exchangeId && !map.has(exchangeId)) {
                map.set(exchangeId, {
                    id: exchangeId,
                    name: exchangeName,
                    value: exchangeValue,
                    parentId: '',
                    parentValue: '',
                    column: 'left',
                    selectedProduct: equals(exchange, props.selection.exchange),
                    expanded: equals(exchange, props.selection.exchange),
                    order: 0,
                    displayOrder: 0,
                    children: [],
                });
            }

            const parentNode = exchangeId ? map.get(exchangeId)! : null;

            // Commodity node
            if (commodityId) {
                let commodityNode = parentNode?.children.find((n) => n.id === commodityId);
                if (!commodityNode) {
                    commodityNode = {
                        id: commodityId,
                        name: commodityName,
                        value: commodityValue,
                        parentId: exchangeId,
                        parentValue: exchangeValue,
                        column: 'left',
                        selectedProduct: equals(exchange, props.selection.exchange) && equals(commodityName, props.selection.commodityName),
                        expanded: false,
                        order: 0,
                        displayOrder: 0,
                        children: [],
                    };
                    parentNode?.children.push(commodityNode);
                }
            }
        });

        return Array.from(map.values());
    }, [props.pageElements, props.selection.commodityName, props.selection.exchange]);

    const findNodeById = (nodes: ITreeNode[], id: string): ITreeNode | undefined =>
        nodes.reduce<ITreeNode | undefined>((found, node) => {
            if (found) {
                return found;
            }
            if (node.id === id) {
                return node;
            }
            return findNodeById(node.children, id);
        }, undefined);

    const commodityTreeNodes: ITreeNode[] = useMemo(() => {
        const map = new Map<string, ITreeNode>();

        props.pageElements.forEach((pageElement) => {
            const { commodity, subCommodity, commodityDisplayName, subCommodityDisplayName } = pageElement;

            const commodityName = commodityDisplayName ? translate(commodityDisplayName) ?? commodityDisplayName : commodity ?? '';
            const commodityId = `${commodityName}`;
            const commodityValue = `${commodityId}`;

            const subCommodityName = subCommodityDisplayName ? translate(subCommodityDisplayName) || subCommodityDisplayName : subCommodity ?? '';
            const subCommodityId = `${commodityId}|${subCommodityName}`;
            const subCommodityValue = `${subCommodityId}`;

            const isSelectedCommodity = equals(commodityName, props.selection.commodityName);

            // Commodity node
            if (!map.has(commodityId)) {
                map.set(commodityId, {
                    id: commodityId,
                    name: commodityName,
                    value: commodityValue,
                    parentId: '',
                    parentValue: '',
                    column: 'left',
                    selectedProduct: isSelectedCommodity,
                    expanded: isSelectedCommodity,
                    order: 0,
                    displayOrder: 0,
                    children: [],
                });
            }

            const commodityNode = map.get(commodityId)!;
            const isSelectedSubCommodity = isSelectedCommodity && equals(subCommodityName, props.selection.subCommodityName);

            // SubCommodity node
            if (subCommodity && !commodityNode.children.find((n) => n.id === subCommodityId)) {
                commodityNode.children.push({
                    id: subCommodityId!,
                    name: subCommodityName,
                    value: subCommodityValue,
                    parentId: commodityId,
                    parentValue: commodityValue,
                    column: 'left',
                    selectedProduct: isSelectedSubCommodity,
                    expanded: false,
                    order: 0,
                    displayOrder: 0,
                    children: [],
                });
            }
        });

        return Array.from(map.values());
    }, [props.pageElements, props.selection.commodityName, props.selection.subCommodityName, props.selection.commodityName]);

    const treeNodes: ITreeNode[] = useMemo(() => {
        if (isMarketIndicators) {
            return marketIndicatorsTreeNodes;
        }
        if (isFutures) {
            return futuresTreeNodes;
        }
        return commodityTreeNodes;
    }, [isMarketIndicators, isFutures, marketIndicatorsTreeNodes, futuresTreeNodes, commodityTreeNodes]);

    const handleSelect = (event: React.MouseEvent<HTMLElement>) => {
        const { id } = event.currentTarget;
        const node = findNodeById(treeNodes, id);
        if (!node) {
            return;
        }

        if (isMarketIndicators) {
            const [exchange, dataSource, commodityName] = node.value.split('|');
            if (exchange === 'null') {
                props.handleExchangeSelect(undefined);
            } else {
                props.handleExchangeSelect(exchange as ExchangeType);
            }
            if (dataSource === 'null') {
                props.handleDataSourceSelect(undefined);
            } else {
                props.handleDataSourceSelect(dataSource as DemeterDataSource);
            }
            if (commodityName === 'null') {
                props.handleCommoditySelect(undefined);
            } else {
                props.handleCommoditySelect(commodityName);
            }
            return;
        }
        if (isFutures) {
            const [exchange, commodityName] = node.value.split('|');

            if (exchange === 'null') {
                props.handleExchangeSelect(undefined);
            } else {
                props.handleExchangeSelect(exchange as ExchangeType);
            }
            if (commodityName === 'null') {
                props.handleCommoditySelect(undefined);
            } else {
                props.handleCommoditySelect(commodityName);
            }

            return;
        }

        const [commodityName, subCommodityName] = node.value.split('|');

        if (commodityName === 'null') {
            props.handleCommoditySelect(undefined);
        } else {
            props.handleCommoditySelect(commodityName);
        }
        if (subCommodityName === 'null') {
            props.handleSubCommoditySelect(undefined);
        } else {
            props.handleSubCommoditySelect(subCommodityName);
        }
    };

    return <TreeView data={treeNodes} searchTerm="" handleExpand={handleSelect} handleSelect={handleSelect} disabledNodes={[]} track={props.track} />;
};

export default PageElementExchangeAndCommodity;
